/**
 * ASK THE CLIENT (R10.1, ADR-0015 §R10).
 *
 * Beside the answer control on every open request: choose (or add) the person
 * at the client who has what is needed, and Leveri issues a link scoped to
 * THIS request alone. The accountant sends it themselves — there is no email
 * service. The link is shown ONCE; afterwards only its state is.
 *
 * What the client sends arrives as their response on this request. It does not
 * answer the request: a person still decides whether it satisfies what was
 * asked (R10.2).
 */
import { useCallback, useEffect, useState } from "react";
import {
  ClientContactResponse, ClientContactsResponse, IssueRequestLinkResponse, RequestLinkResponse, RequestLinksResponse,
  type ClientContact, type OpenRequest, type RequestLink,
} from "@leveri/contracts";
import { Button, Citation, Label, Notice, Select, TextField } from "../../file/index.js";
import { call, failureMessage } from "../../api.js";

export interface AskClientProps { workId: string; clientId: string; request: OpenRequest }


export function AskClient({ workId, clientId, request }: AskClientProps) {
  const [open, setOpen] = useState(false);
  const [contacts, setContacts] = useState<readonly ClientContact[]>([]);
  const [links, setLinks] = useState<readonly RequestLink[]>([]);
  const [contactId, setContactId] = useState("");
  const [newName, setNewName] = useState("");
  const [issued, setIssued] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    const [c, l] = await Promise.all([
      call("GET", `/api/clients/${clientId}/contacts`, ClientContactsResponse),
      call("GET", `/api/work/${workId}/links`, RequestLinksResponse),
    ]);
    setContacts(c.contacts.filter((x) => x.status === "ACTIVE"));
    setLinks(l.links.filter((x) => x.clarificationId === request.clarificationId));
  }, [clientId, workId, request.clarificationId]);

  useEffect(() => { if (open) refresh().catch((e: unknown) => setFailure(failureMessage(e))); }, [open, refresh]);

  async function issue() {
    if (busy) return;
    setBusy(true); setFailure(null); setIssued(null);
    try {
      let chosen = contactId;
      if (!chosen && newName.trim()) {
        chosen = (await call("POST", `/api/clients/${clientId}/contacts`, ClientContactResponse, { displayName: newName.trim() })).contact.id;
        setNewName("");
      }
      if (!chosen) { setFailure("Choose who to ask, or add them."); return; }
      const result = await call("POST", `/api/work/${workId}/requests/${request.requirementId}/links`, IssueRequestLinkResponse, { clientContactId: chosen });
      setIssued(`${window.location.origin}${result.path}`);
      await refresh();
    } catch (e) {
      setFailure(failureMessage(e));
    } finally {
      setBusy(false);
    }
  }

  async function revoke(linkId: string) {
    try {
      await call("POST", `/api/work/${workId}/links/${linkId}/revoke`, RequestLinkResponse, {});
      await refresh();
    } catch (e) {
      setFailure(failureMessage(e));
    }
  }

  if (!open) {
    return (
      <div className="mt-3">
        <Button variant="secondary" size="sm" onClick={() => setOpen(true)} data-testid="ask-client-open">Ask the client</Button>
      </div>
    );
  }

  return (
    <div className="mt-4 border-t border-rule pt-4" data-testid="ask-client">
      <Label>Ask the client</Label>
      <div className="mt-2 flex flex-col gap-3">
        {contacts.length > 0 && (
          <Select label="Who has it" value={contactId} onChange={(e) => setContactId(e.target.value)}>
            <option value="">Someone new…</option>
            {contacts.map((c) => <option key={c.id} value={c.id}>{c.displayName}</option>)}
          </Select>
        )}
        {!contactId && (
          <TextField label={contacts.length > 0 ? "Their name" : "Who has it — their name"} value={newName} onChange={(e) => setNewName(e.target.value)} />
        )}
        <div>
          <Button size="sm" disabled={busy} onClick={() => void issue()} data-testid="ask-client-issue">{busy ? "Issuing…" : "Create a link for this request"}</Button>
        </div>
      </div>

      {issued && (
        <div className="mt-4">
          <p className="text-sm text-ink">Send this link yourself. It is shown only now, opens this request alone, and lasts 14 days.</p>
          <div className="mt-2 flex items-center gap-2">
            <input readOnly value={issued} className="w-full border border-rule bg-transparent px-2 py-1.5 font-mono text-[12px] text-ink" data-testid="ask-client-link" onFocus={(e) => e.currentTarget.select()} />
            <Button size="sm" variant="secondary" onClick={() => void navigator.clipboard?.writeText(issued)}>Copy</Button>
          </div>
          <p className="mt-2 text-[13px] text-stone">What they send arrives here as their response. It does not answer the request — you decide whether it is enough.</p>
        </div>
      )}

      {links.length > 0 && (
        <ul className="mt-4 flex flex-col gap-1.5">
          {links.map((l) => (
            <li key={l.id} className="flex items-baseline justify-between gap-3 text-[13px]">
              <span className="text-ink">
                {l.contactName} <Citation>· {l.summaryText}</Citation>
              </span>
              {l.state === "ACTIVE" && <Button size="sm" variant="secondary" onClick={() => void revoke(l.id)}>Revoke</Button>}
            </li>
          ))}
        </ul>
      )}
      {failure && <Notice className="mt-3">{failure}</Notice>}
    </div>
  );
}
